import { isBefore } from 'date-fns';
import type { GetServerSideProps } from 'next';
import React from 'react';

import { csrClient, ssrClient } from '../../apollo/api/avantArteClient';
import { storefrontClient } from '../../apollo/shopify/storefrontClient';
import MetaTags from '../../components/Content/MetaTags/MetaTags';
import { Page404 } from '../../features/private-sales/components/404';
import { PrivateSales } from '../../features/private-sales/components/PrivateSales';
import { AvantArtePrivateSalesGateway } from '../../features/private-sales/gateways/AvantArtePrivateSalesGateway';
import { AvantArteProductGateway } from '../../gateways/AvantArteProductGateway';
import { ShopifyCheckoutGateway } from '../../gateways/ShopifyCheckoutGateway';
import { ShopifyProductsGateway } from '../../gateways/ShopifyProductsGateway';
import { imageOgStaticLoader } from '../../helpers/og-image';
import { GeneratedPrivateLink } from '../../types/generated';
import { AnalyticsFlowProvider } from '../../utils/analytics/flow';
import { pageTranslations } from '../../utils/translations';

const privateSalesGateway = new AvantArtePrivateSalesGateway(csrClient);
const aaProductsGateway = new AvantArteProductGateway(csrClient);
const checkoutGateway = new ShopifyCheckoutGateway(storefrontClient);
const productsGateway = new ShopifyProductsGateway(storefrontClient);

type PrivateSalePageData = {
  privateLink: GeneratedPrivateLink;
};

interface PropsWithData {
  locale?: string;
  pageData: PrivateSalePageData;
  ogImageUrl: string;
}

type Props =
  | PropsWithData
  | {
      notFound?: boolean;
    };

export default function Page(props: Props) {
  if ('notFound' in props) {
    const { notFound } = props;
    if (notFound) return <Page404 />;
  }

  const { locale, ogImageUrl, pageData } = props as PropsWithData;

  const { expiresAt, passwordRequired, slug } = pageData.privateLink;
  const isExpired = Boolean(
    expiresAt && isBefore(new Date(expiresAt), new Date()),
  );

  return (
    <>
      <MetaTags robots="noindex, nofollow" />
      <AnalyticsFlowProvider
        name="private"
        properties={{
          'private_link_slug': slug,
          'private_status': isExpired ? 'expired' : 'open',
          'unlock_mechanism': passwordRequired ? 'code' : 'open',
        }}
        sendViewedEvent
      >
        {() => (
          <PrivateSales
            aaProductsGateway={aaProductsGateway}
            checkoutGateway={checkoutGateway}
            isExpired={isExpired}
            locale={locale!}
            ogImageUrl={ogImageUrl}
            privateLink={pageData.privateLink}
            privateSalesGateway={privateSalesGateway}
            productsGateway={productsGateway}
          />
        )}
      </AnalyticsFlowProvider>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const { locale, params, preview = false } = ctx;
  const slug = params?.slug ? params.slug[params.slug.length - 1] : '';

  const { privateLink } = await new AvantArtePrivateSalesGateway(
    ssrClient(ctx),
  ).getPrivateLink(slug);

  if (!privateLink) {
    // eslint-disable-next-line no-param-reassign
    ctx.res.statusCode = 404;
    return {
      props: {
        notFound: true,
        ...(await pageTranslations(locale!, ['private'])),
      },
    };
  }

  const ogImageUrl = await imageOgStaticLoader({
    overlineKey: { key: 'og:private.overline' },
    titleKey: privateLink.heading,
    imageSrc: privateLink.imageUrl,
    imageWidth: 510,
    imageHeight: 510,
    isDark: true,
    locale,
  });

  return {
    props: {
      pageData: {
        privateLink,
      },
      preview,
      locale,
      ogImageUrl,
      ...(await pageTranslations(locale!, ['private', 'og'])),
    },
  };
};
